import { useCallback, useEffect, useState } from "react";
import { Gauge } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { usd as money } from "../lib/money";

export const SPEND_EVENT = "duckify-spend-changed";
const POLL_MS = 45000;

// The signed-in maker's "used $x of $cap" generation meter (owner / exempt accounts: this month). Hidden when signed out.
export const useSpendMeter = () => {
  const { isAuthed } = useAuth();
  const [meter, setMeter] = useState(null);
  const load = useCallback(async () => {
    if (!isAuthed) return setMeter(null);
    try {
      const { data } = await api.get("/auth/me/spend");
      setMeter(data);
    } catch {
      /* keep the last reading */
    }
  }, [isAuthed]);
  useEffect(() => {
    load();
    window.addEventListener(SPEND_EVENT, load);
    const t = setInterval(load, POLL_MS);
    return () => {
      window.removeEventListener(SPEND_EVENT, load);
      clearInterval(t);
    };
  }, [load]);
  return meter && meter.show ? meter : null;
};

export const SpendMeter = ({ variant = "menu" }) => {
  const meter = useSpendMeter();
  if (!meter) return null;
  const tone = meter.reached ? "text-rose-600" : meter.pct >= 80 ? "text-accent" : "text-muted-foreground";
  const bar = meter.reached ? "bg-rose-500" : meter.pct >= 80 ? "bg-accent" : "bg-primary";
  if (variant === "line") {
    return (
      <p data-testid="spend-meter-line" className={`mt-3 text-center text-xs font-mono2 ${tone}`}>
        Generation allowance: used {money(meter.used_usd)} of {money(meter.cap_usd)}{meter.window === "month" ? " this month" : ""}
        {meter.reached ? " · limit reached — order a print or contact us to top up" : ""}
      </p>
    );
  }
  return (
    <div data-testid="spend-meter" className="px-2 py-1.5">
      <div className={`flex items-center justify-between gap-2 text-xs ${tone}`}>
        <span className="inline-flex items-center gap-1.5 font-medium">
          <Gauge className="h-3.5 w-3.5" /> Allowance
        </span>
        <span data-testid="spend-meter-text" className="font-mono2">
          used {money(meter.used_usd)} of {money(meter.cap_usd)}{meter.window === "month" ? " this month" : ""}
        </span>
      </div>
      <div className="mt-1.5 h-1.5 w-full rounded-full bg-muted overflow-hidden" role="progressbar" aria-valuenow={meter.pct} aria-valuemin={0} aria-valuemax={100}>
        <div data-testid="spend-meter-bar" className={`h-full rounded-full ${bar} transition-[width]`} style={{ width: `${Math.min(100, meter.pct)}%` }} />
      </div>
      {meter.reached && (
        <p data-testid="spend-meter-reached" className="mt-1.5 text-[11px] leading-snug text-rose-600">
          Limit reached — your ducks keep working; order a print or contact us to top up.
        </p>
      )}
    </div>
  );
};
